import React, { useState } from 'react';
import './Tools.css';
import PasswordTool from './PasswordTool';

const ORDER_FORM_URL = `${process.env.PUBLIC_URL || ''}/documents/bon-de-commande-modifiable.pdf`;

const parseAmount = value => {
  if (String(value).trim() === '') return null;
  const amount = Number(String(value).replace(',', '.'));
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

export function calculateMargin(purchasePrice, sellingPrice, quantity) {
  const purchase = parseAmount(purchasePrice);
  const selling = parseAmount(sellingPrice);
  const count = parseAmount(quantity);
  if (purchase === null || selling === null || count === null || !Number.isInteger(count) || count < 1) return null;

  const unitMargin = selling - purchase;
  return {
    sellingPrice: selling,
    unitMargin,
    totalMargin: unitMargin * count,
    marginRate: purchase > 0 ? unitMargin / purchase * 100 : null,
    brandRate: selling > 0 ? unitMargin / selling * 100 : null,
  };
}

export function calculateSuggestedSellingPrice(purchasePrice, desiredMargin, marginType) {
  const purchase = parseAmount(purchasePrice);
  const desired = parseAmount(desiredMargin);
  if (purchase === null || desired === null || !['percent', 'euro'].includes(marginType) || (marginType === 'percent' && desired >= 100)) return null;

  const price = marginType === 'percent' ? purchase / (1 - desired / 100) : purchase + desired;
  return Number.isFinite(price) && Number.isFinite(price * 100) ? Math.ceil(price * 100 - 1e-9) / 100 : null;
}

const currency = value => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
const percent = value => value === null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value) + ' %';
export default function Tools() {
  const [tool, setTool] = useState('signature');
  const [purchasePrice, setPurchasePrice] = useState('70');
  const [sellingPrice, setSellingPrice] = useState('100');
  const [quantity, setQuantity] = useState('1');
  const [desiredMargin, setDesiredMargin] = useState('30');
  const [marginType, setMarginType] = useState('percent');
  const result = calculateMargin(purchasePrice, sellingPrice, quantity);
  const suggestedPrice = calculateSuggestedSellingPrice(purchasePrice, desiredMargin, marginType);
  return <section className="cms-tools">
    <header><span>AM SERVICES · AU QUOTIDIEN</span><h1>Vos outils</h1><p>Vos outils du quotidien, directement dans le CMS.</p></header>
    <nav className="tools-bar" aria-label="Choisir un outil">
      <button type="button" aria-pressed={tool === 'signature'} onClick={() => setTool('signature')}>✉️ Signature mail</button>
      <button type="button" aria-pressed={tool === 'margin'} onClick={() => setTool('margin')}>📈 Calculateur de marge</button>
      <button type="button" aria-pressed={tool === 'password'} onClick={() => setTool('password')}>🔐 Mot de passe</button>
      <button type="button" aria-pressed={tool === 'order-form'} onClick={() => setTool('order-form')}>📋 Bon de commande</button>
    </nav>
    <div className="signature-tool" hidden={tool !== 'signature'}><iframe title="Générateur de signature mail AM Services" src="https://cms.amservices.eu/generateur.php" /></div>
    <section className="order-form-tool" hidden={tool !== 'order-form'} aria-label="Bon de commande modifiable">
      <div className="order-form-toolbar">
        <div><h2>Bon de commande</h2><p>Complétez les champs du document ci-dessous, puis enregistrez le PDF depuis la barre du lecteur. Les CGV figurent au verso.</p></div>
        <div className="order-form-actions"><a href={ORDER_FORM_URL} target="_blank" rel="noopener noreferrer">Ouvrir en grand</a><a href={ORDER_FORM_URL} download="bon-de-commande-AM-SERVICES-modifiable.pdf">Télécharger le modèle vierge</a></div>
      </div>
      <iframe title="Bon de commande AM SERVICES et CGV, formulaire PDF modifiable" src={ORDER_FORM_URL} />
    </section>
    {tool === 'password' && <PasswordTool />}
    <section className="margin-tool" hidden={tool !== 'margin'}>
      <h2>Calculateur de marge</h2>
      <p>Comparez votre prix d’achat et votre prix de vente hors taxes, ou définissez directement la marge à appliquer.</p>
      <div className="margin-fields">
        <label>Prix d’achat HT (€)<input type="text" inputMode="decimal" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} /></label>
        <label>Prix de vente HT (€)<input type="text" inputMode="decimal" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)} /></label>
        <label>Quantité<input type="number" min="1" step="1" inputMode="numeric" value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
      </div>
      <div className="margin-target">
        <h3>Combien de marge appliquer ?</h3>
        <p>Le pourcentage représente la part de marge dans le prix de vente. Exemple : 70 € d’achat + 30 % de marge = 100 € HT de vente.</p>
        <div className="margin-target-fields">
          <label>Marge souhaitée<input type="text" inputMode="decimal" value={desiredMargin} onChange={e => setDesiredMargin(e.target.value)} /></label>
          <label>Unité<select value={marginType} onChange={e => setMarginType(e.target.value)}><option value="percent">% du prix de vente (taux de marque)</option><option value="euro">€ par unité</option></select></label>
        </div>
        <div className="margin-target-result" aria-live="polite">
          {suggestedPrice === null ? <p role="alert">Saisissez un prix d’achat positif et une marge comprise entre 0 et 99,99 % (ou un montant en euros positif).</p> : <>
            <span>Prix de vente HT conseillé</span>
            <strong>{currency(suggestedPrice)}</strong>
            <button type="button" onClick={() => setSellingPrice(String(suggestedPrice))}>Appliquer ce prix de vente</button>
          </>}
        </div>
      </div>
      <div aria-live="polite">{result ? <>
        <dl className="margin-results">
          <div><dt>Prix de vente HT</dt><dd>{currency(result.sellingPrice)}</dd><small>Par unité</small></div>
          <div className={result.unitMargin < 0 ? 'margin-negative' : ''}><dt>Marge brute</dt><dd>{currency(result.unitMargin)}</dd><small>Par unité</small></div>
          <div className={result.totalMargin < 0 ? 'margin-negative' : ''}><dt>Marge totale</dt><dd>{currency(result.totalMargin)}</dd><small>Pour {quantity} unité{Number(quantity) > 1 ? 's' : ''}</small></div>
          <div><dt>Taux de marge</dt><dd>{percent(result.marginRate)}</dd><small>Marge ÷ prix d’achat HT</small></div>
          <div><dt>Taux de marque</dt><dd>{percent(result.brandRate)}</dd><small>Marge ÷ prix de vente HT</small></div>
        </dl>
        {(result.marginRate === null || result.brandRate === null) && <p className="margin-note">Un taux affiche « — » lorsque son prix de référence est égal à zéro.</p>}
      </> : <p role="alert">Saisissez des prix positifs et une quantité entière supérieure à zéro.</p>}</div>
    </section>
  </section>;
}
